import React, { FC, useState } from "react";
import { Plan, PlanStatus } from "api/entities/Plan";
import {
  EuiBadge, EuiButtonEmpty,
  EuiCard, EuiFlexGroup, EuiFlexItem,
  EuiIcon,
  EuiLink,
  EuiSpacer,
  EuiText,
} from "@elastic/eui";
import { PlanGetTotalRecurringPrice } from "../../utils/PlanUtils";
import moment from "moment";
import { NumberToCurrency } from "../../utils/Formatters";
import {useEndBusinessPlanMutation, useReactivateBusinessPlanMutation} from "api/rtkQueryApi/BusinessesApi";
import { ESignTokenGenerator } from "components/payments/ESignTokenGenerator";
import { Business } from "api/entities/Business";
import { AddonBillingType } from "api/entities/Addon";

export interface IPlanDetailsCard {
  business: Business;
  plan: Plan;
  onAddAddonClick?: (plan: Plan) => void;
}

export const PlanDetailsCard: FC<IPlanDetailsCard> = (props) => {
  const [showAddonDetails, setShowAddonDetails] = useState(false);
  const productDetails = props.plan.productDetails;
  const billingSchedule = props.plan.schedule;
  const recurringPrice = PlanGetTotalRecurringPrice(props.plan);
  const [reactivateBusinessPlan, reactivateBusinessPlanRequest] =
    useReactivateBusinessPlanMutation();
  const [endBusinessPlan, endBusinessPlanRequest] = useEndBusinessPlanMutation();

  const onAddAddonClick = () => {
    if (props.onAddAddonClick) {
      props.onAddAddonClick(props.plan);
    }
  };

  const getBadgeColor = (status: PlanStatus) => {
    switch (status) {
      case PlanStatus.Active:
      case PlanStatus.Ending:
        return "success";
      case PlanStatus.PaymentFailed:
        return "danger";
      default:
        return "default";
    }
  };

  const reactivatePlanClick = () => {
    reactivateBusinessPlan({
      businessId: props.plan.businessId,
      planId: props.plan.id,
    });
  };

  const endPlanClick = () => {
    endBusinessPlan({
      businessId: props.plan.businessId,
      planId: props.plan.id,
    });
  };

  const onScheduleCampaignAdvisorClick = () => {
    window.open("https://calendly.com/eburklow-adcritter/advertising-kick-off-meeting", "_blank");
  }

  const onScheduleAccountManagerClick = () => {
    window.open("https://calendly.com/mike-db130/30min", "_blank");
  }

  return (
    <EuiCard
      title={props.plan.name}
      description={
        <EuiText size={"s"}>
          <p>
            <b>{productDetails.name}:</b> {productDetails.description}
          </p>
        </EuiText>
      }
      textAlign={"left"}
    >
      <EuiBadge color={getBadgeColor(props.plan.status)}>
        {props.plan.status}
      </EuiBadge>
      <EuiSpacer size={"xs"} />
      {props.plan.status === PlanStatus.PendingSignature && (
        <>
          <ESignTokenGenerator business={props.business} plan={props.plan} />
        </>
      )}
      {props.plan.status === PlanStatus.Active && (
          <EuiFlexGroup>
            <EuiFlexItem grow={false}>
              <EuiButtonEmpty
                  size={"xs"}
                  style={{ paddingLeft: 0 }}
                  onClick={onScheduleAccountManagerClick}
                  iconType={'calendar'}
              >
                Schedule Account Manager
              </EuiButtonEmpty>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiButtonEmpty
                  size={"xs"}
                  style={{ paddingLeft: 0 }}
                  onClick={onScheduleCampaignAdvisorClick}
                  iconType={'calendar'}

              >
                Schedule Campaign Advisor
              </EuiButtonEmpty>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiButtonEmpty
                  size={"xs"}
                  style={{ paddingLeft: 0 }}
                  onClick={endPlanClick}
                  iconType={'stop'}
                  isLoading={endBusinessPlanRequest.isLoading}
              >
                End Plan
              </EuiButtonEmpty>
            </EuiFlexItem>
          </EuiFlexGroup>
      )}
      <EuiSpacer size={"s"} />
      <EuiText size={"xs"}>
        {billingSchedule.intervalsCompleted ?? 0} /{" "}
        {billingSchedule.totalIntervals} {billingSchedule.intervalUnit}s
      </EuiText>
      <EuiText size={"xs"}>
        {NumberToCurrency(recurringPrice)} / {billingSchedule.intervalUnit}
      </EuiText>
      <EuiText size={"xs"}>
        Start Date:{" "}
        {moment(billingSchedule.eventStartDate).format("MM/DD/yyyy")}
      </EuiText>
      <EuiText size={"xs"}>
        Next Event Date:{" "}
        {billingSchedule.nextEventDate
          ? moment(billingSchedule.nextEventDate).format("MM/DD/yyyy")
          : ""}
      </EuiText>
      <EuiSpacer size={"xs"} />
      {showAddonDetails && (
        <>
          <EuiSpacer size={"s"} />
          <EuiText>
            <p>
              <b>Addons</b>{" "}
              {props.onAddAddonClick && (
                <EuiLink>
                  <EuiIcon
                    type={"plusInSquare"}
                    size={"s"}
                    onClick={onAddAddonClick}
                  />
                </EuiLink>
              )}
            </p>
          </EuiText>
          {props.plan.addonDetailsList &&
            props.plan.addonDetailsList.map((addonDetails) => (
              <div style={{ paddingLeft: 12 }}>
                <EuiSpacer size={"xs"} />
                <EuiText size={"s"}>{addonDetails.name}</EuiText>
                <EuiText size={"xs"}>
                  {NumberToCurrency(addonDetails.price)} (
                  {addonDetails.addonBillingType ===
                    AddonBillingType.ParentRecurring &&
                    "Included in plan monthly"}
                  {addonDetails.addonBillingType ===
                    AddonBillingType.IndividualRecurring && "Billed Separately"}
                  )
                </EuiText>
                {addonDetails.customizableStartDateAndContractLength && (
                  <>
                    <EuiText size={"xs"}>
                      Start Date:{" "}
                      {moment(addonDetails.startDate).format("MM/DD/yyyy")}
                    </EuiText>
                    <EuiText size={"xs"}>
                      Contract Length: {addonDetails.contractLength} month
                      {addonDetails.contractLength !== 1 ? "s" : ""}
                    </EuiText>
                  </>
                )}
              </div>
            ))}
          <EuiSpacer size={"s"} />
        </>
      )}
      <EuiSpacer size={"xs"} />
      <EuiLink onClick={() => setShowAddonDetails(!showAddonDetails)}>
        {showAddonDetails ? "Hide" : "Show"} Addons (
        {props.plan.addonDetailsList.length})
      </EuiLink>

      {props.plan.status === PlanStatus.PaymentFailed && (
        <>
          <EuiSpacer size={"xs"} />

          {reactivateBusinessPlanRequest.isLoading && (
            <EuiText size={"xs"}>Reactivating...</EuiText>
          )}

          {!reactivateBusinessPlanRequest.isLoading && (
            <EuiLink onClick={reactivatePlanClick}>Reactivate Plan</EuiLink>
          )}
        </>
      )}
    </EuiCard>
  );
};
